import CardStats from 'components/CardStats/CardStats';
import DatePickerEs from 'components/DatePickerEs/DatePickerEs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { contractsI18n } from './i18n';
import './contracts.css';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import {
  approveFeeIncreaseRequest,
  exportContracts,
  getContractCatalogs,
  getContracts,
  getContractsKpis,
  getFeeIncreaseRequests,
} from '../../services/contractGeneratorService';

const Contracts = ({ DesignSystemComponents }) => {
  const { Card, Table, Button, Input, Dropdown, IconButton, Badge } =
    DesignSystemComponents;

  const navigate = useNavigate();
  const { t } = useScopedTranslation(contractsI18n);

  const STATUS_LABELS = useMemo(
    () => ({
      Draft: t('ct_status_draft'),
      Sent: t('ct_status_sent'),
      SignedByOne: t('ct_status_signedByOne'),
      Signed: t('ct_status_signed'),
      Terminated: t('ct_status_terminated'),
      Canceled: t('ct_status_canceled'),
      1: t('ct_status_draft'),
      2: t('ct_status_sent'),
      3: t('ct_status_signedByOne'),
      4: t('ct_status_signed'),
      5: t('ct_status_terminated'),
      6: t('ct_status_canceled'),
    }),
    [t]
  );

  const [filterByDate, setFilterByDate] = useState(null);
  const [searchContract, setSearchContract] = useState('');
  const [selectedBusinessLine, setSelectedBusinessLine] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [businessLineOptions, setBusinessLineOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusLabelById, setStatusLabelById] = useState({});
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({
    pending: 0,
    canceled: 0,
    signed: 0,
    terminated: 0,
  });
  const [page, setPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirmAuthorization, setConfirmAuthorization] = useState(false);
  const [pendingIncreaseByContractId, setPendingIncreaseByContractId] =
    useState(new Map());
  const [selectedIncreaseRequest, setSelectedIncreaseRequest] = useState(null);

  const queryParams = useMemo(() => {
    const dateFrom = filterByDate?.startDate || filterByDate?.start || null;
    const dateTo = filterByDate?.endDate || filterByDate?.end || null;
    return {
      page: page + 1,
      pageSize: 5,
      sortBy: 'dateContract',
      desc: true,
      search: searchContract?.trim() || undefined,
      businessLineId: selectedBusinessLine?.value || undefined,
      status: selectedStatus?.value || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    };
  }, [filterByDate, page, searchContract, selectedBusinessLine, selectedStatus]);

  const loadCatalogs = useCallback(async () => {
    try {
      const response = await getContractCatalogs();
      const lines = (response?.businessLines || []).map((item) => ({
        label: item.label,
        value: item.id,
      }));
      setBusinessLineOptions([{ label: t('ct_businessLineAll'), value: null }, ...lines]);

      const statusMap = {};
      const statuses = (response?.statuses || []).map((item) => {
        const label = STATUS_LABELS[item.code] || item.label || item.code;
        statusMap[String(item.id)] = label;
        return {
          label,
          value: item.id,
          code: item.code,
        };
      });
      setStatusLabelById(statusMap);
      setStatusOptions([{ label: t('ct_statusAll'), value: null, code: null }, ...statuses]);
    } catch (error) {
      console.error(error);
    }
  }, [STATUS_LABELS, t]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [contractsResponse, kpisResponse, feeIncreaseResponse] =
        await Promise.all([
          getContracts(queryParams),
          getContractsKpis(queryParams),
          getFeeIncreaseRequests({ page: 1, pageSize: 200, statusId: 1 }),
        ]);

      const increaseMap = new Map();
      (feeIncreaseResponse?.items || []).forEach((item) => {
        increaseMap.set(item.contractId, item);
      });
      setPendingIncreaseByContractId(increaseMap);

      const mappedRows = (contractsResponse?.items || []).map((item) => {
        const statusIdKey =
          item?.statusId != null
            ? String(item.statusId)
            : item?.status != null
              ? String(item.status)
              : null;
        const rawStatus =
          STATUS_LABELS[item.status] ??
          (statusIdKey ? statusLabelById[statusIdKey] : undefined) ??
          item.statusLabel ??
          item.estado ??
          item.statusName ??
          item.status ??
          '--';
        const baseStatus =
          typeof rawStatus === 'string' ? rawStatus : String(rawStatus ?? '--');
        const pendingIncrease = increaseMap.has(item.id);
        return {
          id: item.id,
          code: item.code,
          defendido: item.clientName || '--',
          rut: item.clientIdentifier || '--',
          monto:
            item.amount == null
              ? '--'
              : new Intl.NumberFormat('es-CL', {
                  style: 'currency',
                  currency: 'CLP',
                  maximumFractionDigits: 0,
                }).format(Number(item.amount)),
          abogado: '--',
          creacion: item.dateContract
            ? new Date(item.dateContract).toLocaleDateString()
            : '--',
          estado: pendingIncrease ? t('ct_status_pendingFeeAuth') : baseStatus,
          hasPendingIncrease: pendingIncrease,
        };
      });

      setRows(mappedRows);
      setTotalRows(contractsResponse?.total || 0);
      setKpis({
        pending: kpisResponse?.pending || 0,
        canceled: kpisResponse?.canceled || 0,
        signed: kpisResponse?.signed || 0,
        terminated: kpisResponse?.terminated || 0,
      });
    } catch (error) {
      console.error(error);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [queryParams, statusLabelById, STATUS_LABELS, t]);

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async () => {
    try {
      const blob = await exportContracts(queryParams);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contratos-${new Date().toISOString().slice(0, 19)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    }
  };

  const handleConfirmAuthorization = async () => {
    if (!selectedIncreaseRequest?.id) return;
    try {
      await approveFeeIncreaseRequest(selectedIncreaseRequest.id, {});
      setConfirmAuthorization(false);
      setSelectedIncreaseRequest(null);
      await loadData();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div>
      <div className='mb-4 d-flex justify-content-between'>
        <div>
          <h2 className='fw-semibold'>{t('ct_pageTitle')}</h2>
          <h4 className='fw-normal'>{t('ct_pageSubtitle')}</h4>
        </div>
        <Button
          label={t('ct_generateContract')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={() => navigate('/generador-contratos/generador')}
        />
      </div>
      <div className='row mb-4'>
        <div className='col-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('ct_statPending')}
            count={String(kpis.pending)}
            subtitle=''
          />
        </div>
        <div className='col-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('ct_statCanceled')}
            count={String(kpis.canceled)}
            subtitle=''
          />
        </div>
        <div className='col-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('ct_statSigned')}
            count={String(kpis.signed)}
            subtitle=''
          />
        </div>
        <div className='col-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('ct_statTerminated')}
            count={String(kpis.terminated)}
            subtitle=''
          />
        </div>
      </div>
      <Card specialPadding='px-4 pt-4 pb-2' className='mb-4'>
        <div className='row'>
          <div className='col-3'>
            <Input
              icon='search'
              name='searchContract'
              onChange={(event) => {
                setSearchContract(event.target.value);
                setPage(0);
              }}
              placeholder={t('ct_searchPlaceholder')}
              type='text'
              value={searchContract}
            />
          </div>
          <div className='col-2'>
            <Dropdown
              onChange={(option) => {
                setSelectedBusinessLine(option?.value ? option : null);
                setPage(0);
              }}
              options={businessLineOptions}
              placeholder={t('ct_businessLinePlaceholder')}
              value={selectedBusinessLine || '0'}
            />
          </div>
          <div className='col-2'>
            <Dropdown
              onChange={(option) => {
                setSelectedStatus(option?.value ? option : null);
                setPage(0);
              }}
              options={statusOptions}
              placeholder={t('ct_statusPlaceholder')}
              value={selectedStatus || '0'}
            />
          </div>
          <div className='col-3'>
            <DatePickerEs
              initialRange={filterByDate}
              value={filterByDate}
              onChange={(range) => {
                setFilterByDate(range);
                setPage(0);
              }}
            />
          </div>
          <div className='col-2 d-flex justify-content-end align-items-center'>
            <Button
              iconStart='download'
              label={t('ct_downloadExcel')}
              variant='naked'
              size='sm'
              className='mb-4'
              onClick={handleExport}
            />
          </div>
        </div>
        <div className='table-container table-contracts'>
          <Table
            columns={[
              { accessor: 'code', label: t('ct_col_code'), sort: true },
              {
                accessor: 'client',
                label: t('ct_col_client'),
                sort: true,
                render: (row) => (
                  <div className='client-double'>
                    <p className='caption fw-normal ellipsis'>
                      {row.defendido}
                    </p>
                    <p className='mini fw-normal '>{row.rut}</p>
                  </div>
                ),
              },
              { accessor: 'monto', label: t('ct_col_amount'), sort: true },
              { accessor: 'abogado', label: t('ct_col_lawyer'), sort: true },
              { accessor: 'creacion', label: t('ct_col_createdAt'), sort: true },
              {
                accessor: 'estado',
                label: t('ct_col_status'),
                sort: true,
                render: (row) => {
                  const estadoText =
                    typeof row?.estado === 'string'
                      ? row.estado
                      : String(row?.estado ?? '');
                  const normalizedEstado = estadoText
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase();

                  const badgeColor = (() => {
                    if (normalizedEstado.includes('cancelado') || normalizedEstado.includes('canceled')) return 'error';
                    if (normalizedEstado.includes('finiquitado') || normalizedEstado.includes('terminated')) return 'error';
                    if (normalizedEstado.includes('firmado') || normalizedEstado.includes('signed')) return 'success';
                    if (normalizedEstado.includes('borrador') || normalizedEstado.includes('draft')) return 'light';
                    if (normalizedEstado.includes('enviado') || normalizedEstado.includes('sent')) return 'warning';
                    if (normalizedEstado.includes('pendiente') || normalizedEstado.includes('pending')) return 'warning';
                    if (normalizedEstado.includes('paso') || normalizedEstado.includes('step')) return 'warning';
                    return 'light';
                  })();

                  return (
                    <Badge
                      text={estadoText}
                      color={badgeColor}
                      size='sm'
                    />
                  );
                },
              },
              {
                label: t('ct_col_actions'),
                render: (row) => (
                  <div className='d-flex'>
                    <IconButton
                      ariaLabel={t('ct_aria_goToContract')}
                      icon='eye'
                      size='md'
                      iconStroke='var(--primary-500)'
                      onClick={() =>
                        navigate('/generador-contratos/generador', {
                          state: { contractId: row.id },
                        })
                      }
                    />
                    {row.hasPendingIncrease && (
                      <IconButton
                        ariaLabel={t('ct_aria_authorizeFees')}
                        icon='check2Circle'
                        size='md'
                        iconStroke='var(--success-500)'
                        className='ms-1'
                        onClick={() => {
                          setSelectedIncreaseRequest(
                            pendingIncreaseByContractId.get(row.id)
                          );
                          setConfirmAuthorization(true);
                        }}
                      />
                    )}
                  </div>
                ),
              },
            ]}
            data={rows}
            page={page}
            onPageChange={setPage}
            paginationMode='server'
            totalItems={totalRows}
            initialPageSize={5}
            paginator
            paginatorGroup={[
              { label: '5', value: 5 },
              { label: '10', value: 10 },
            ]}
            loading={loading}
          />
        </div>
      </Card>
      <ConfirmModal
        Modal={DesignSystemComponents.Modal}
        show={confirmAuthorization}
        title={t('ct_confirm_title')}
        message={t('ct_confirm_message')}
        confirmLabel={t('ct_confirm_btn')}
        confirmVariant='success'
        onConfirm={handleConfirmAuthorization}
        onClose={() => {
          setConfirmAuthorization(false);
          setSelectedIncreaseRequest(null);
        }}
      />
    </div>
  );
};

export default Contracts;
